import {
  FC,
  MutableRefObject,
  createRef,
  forwardRef,
  useEffect,
  useRef,
  useState
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MenuItem, Tooltip } from '@mui/material';

import { MDTypography } from '@components';

import CustomSelectRoot from './CustomSelectRoot';
import ExpandMore from '@mui/icons-material/ExpandMore';

import './styles.scss';

const CustomSelect: FC<any> = forwardRef(
  (
    {
      color,
      variant,
      placeholder,
      options,
      value,
      handleValueChange,
      handleClearValue,
      disabled,
      width,
      tooltipText = '',
      axis,
      disabledTooltipText = '',
      isPreview,
      isAddChart = false,
      xAxisColumnName,
      ...rest
    },
    ref
  ) => {
    type TypographyRef = MutableRefObject<HTMLSpanElement | null>;

    const typographyRefs = useRef<TypographyRef[]>([]);

    // Ensure that the length of typographyRefs matches the length of options
    useEffect(() => {
      typographyRefs.current = Array(options.length)
        .fill(undefined)
        .map((_, i) => typographyRefs.current[i] || createRef());
    }, [options]);

    const handleDisable = (option: any) => {
      if (option?.disabled) return true;

      return false;
    };

    const [isOpen, setOpen] = useState(false);

    const closeTooltip = () => {
      if (isOpen) setTimeout(() => setOpen(false), 1000);
    };

    const handleDisabledToolTip = (option: any) => {
      if (disabledTooltipText) return disabledTooltipText;

      if (axis) return `A ${option?.type} cannot be selected here`;
    };

    useEffect(() => closeTooltip(), [isOpen]);

    const getTooltip = (option: any, index: number) => {
      const currentRef = typographyRefs.current[index]?.current;
      let showTooltip = false;

      if (currentRef)
        if (currentRef.scrollWidth > currentRef.clientWidth) showTooltip = true;

      return handleDisable(option)
        ? handleDisabledToolTip(option)
        : option?.label === 'rows' && option?.value !== value
        ? `To view the row count for ${xAxisColumnName}, choose this option. `
        : showTooltip
        ? option?.label
        : '';
    };

    return (
      <Tooltip
        title={tooltipText}
        placement='top'
        enterNextDelay={500}
        open={isOpen}
        onMouseEnter={() => setOpen(true)}
        sx={{ pointerEvents: isPreview ? 'none' : null }}
      >
        <CustomSelectRoot
          {...rest}
          ref={ref}
          ownerState={{
            color,
            variant,
            width,
            value,
            isInvalid:
              !isPreview &&
              handleDisable(options?.find((item: any) => item?.value === value))
          }}
          value={value || ''}
          onChange={handleValueChange}
          displayEmpty
          MenuProps={{
            anchorOrigin: {
              vertical: 32,
              horizontal: 0
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left'
            },
            sx: {
              maxWidth: '330px',
              maxHeight: '308px'
            }
          }}
          SelectDisplayProps={{
            sx: {
              height: '100% !important'
            }
          }}
          disabled={disabled}
          renderValue={
            value !== ''
              ? undefined
              : () => (
                  <MDTypography
                    variant='sm'
                    color={isAddChart && !disabled ? 'secondary' : 'grey'}
                    sx={{
                      paddingInline: '1.5rem',
                      fontSize: { sm: '0.875rem', xs: '0.675rem' }
                    }}
                    fontWeight='bold'
                    textAlign={isAddChart ? 'center' : ''}
                    textGradient={isAddChart && !disabled}
                  >
                    {placeholder}
                  </MDTypography>
                )
          }
          inputProps={{
            ...((isPreview || disabled) && { IconComponent: () => null })
          }}
          IconComponent={ExpandMore}
        >
          {isPreview && (
            <MenuItem key={value} value={value} sx={{ padding: '0px' }}>
              <MDTypography
                color='secondary'
                variant='sm'
                sx={{ textTransform: 'capitalize' }}
                fontWeight='bold'
              >
                {value}
              </MDTypography>
            </MenuItem>
          )}
          {options?.map((option: any, index: number) => (
            <MenuItem
              key={index}
              value={option?.value}
              disableRipple={handleDisable(option)}
              sx={({ palette }) => ({
                padding: '0px',
                borderBottom:
                  option?.label === 'rows'
                    ? `1px solid ${palette.secondary.main}`
                    : 'none',
                borderRadius: '0px'
              })}
              disabled={handleDisable(option)}
            >
              <Tooltip
                placement='right'
                title={getTooltip(option, index)}
                disableInteractive
              >
                <MDTypography
                  ref={typographyRefs.current[index]}
                  component='span'
                  style={{
                    padding: '0.3rem 0.8rem',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textTransform: 'capitalize',
                    textOverflow: 'ellipsis'
                  }}
                  role='presentation'
                  className='custom-select-text'
                  onClick={(e: any) => {
                    if (handleDisable(option)) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  variant='sm'
                  fontWeight='bold'
                  textGradient
                  color='secondary'
                >
                  {option?.icon && (
                    <>
                      <FontAwesomeIcon icon={option?.icon} />
                      &nbsp;&nbsp;
                    </>
                  )}
                  {option?.label}
                </MDTypography>
              </Tooltip>
            </MenuItem>
          ))}
        </CustomSelectRoot>
      </Tooltip>
    );
  }
);

CustomSelect.defaultProps = {
  color: 'secondary',
  variant: 'filled',
  axis: null
};

export default CustomSelect;
